import { decamelizeKeys } from 'humps'
import { Commit } from 'vuex'

import Review from '@/models/review'

import axios from '@/axios-module'
import errorHandler from '@/error-handler'
import notification, { notifications } from '@/notification'

interface State {
  reviewIds: number[]
  reviewEntities: { [id: number]: Review }
  currentCustomerReviewId: number | null
  reviewLoaded: boolean
  reviewSubmitting: boolean
}

const initialState: State = {
  reviewIds: [],
  reviewEntities: {},
  currentCustomerReviewId: null,
  reviewLoaded: false,
  reviewSubmitting: false,
}

export enum ReviewActionTypes {
  GetCustomerReview = '[Review] GetCustomerReview',
  Submit = '[Review] Submit',
}

export enum ReviewMutationTypes {
  LoadStarted = '[Messages] LoadStarted',
  GetCustomerReviewSuccess = '[Review] GetCustomerReviewSuccess',
  GetCustomerReviewFailure = '[Review] GetCustomerReviewFailure',
  SubmitStart = '[Review] SubmitStart',
  SubmitEnd = '[Review] SubmitEnd',
  SubmitSuccess = '[Review] SubmitSuccess',
  SubmitFailure = '[Review] SubmitFailure',
}

export default {
  state: initialState,
  getters: {
    getCurrentCustomerReview(state: State): Review | null {
      return state.currentCustomerReviewId
        ? state.reviewEntities[state.currentCustomerReviewId]
        : null
    },
  },
  actions: {
    async [ReviewActionTypes.GetCustomerReview](
      { commit }: { commit: Commit },
      {
        bookingId,
        token,
      }: { bookingId: number | string; token?: string },
    ) {
      commit(ReviewMutationTypes.LoadStarted)
      const response = await axios.get(`/customer/bookings/${bookingId}/review`, {
        params: token ? { token } : {},
        withCredentials: !token,
      })
      commit(ReviewMutationTypes.GetCustomerReviewSuccess, response.data)
    },
    async [ReviewActionTypes.Submit](
      { commit, state }: { commit: Commit; state: State },
      {
        review,
        bookingId,
        images,
        token,
      }: {
        review: Review
        bookingId: number | string
        images: File[]
        token?: string
      },
    ) {
      if (state.reviewSubmitting) {
        return
      }
      commit(ReviewMutationTypes.SubmitStart)
      try {
        const formData = new FormData()
        const reviewFields: Record<string, any> = { ...review }

        const snakeReviewFields = decamelizeKeys(reviewFields) as Record<
          string,
          unknown
        >

        Object.entries(snakeReviewFields).forEach(([key, value]) => {
          if (value != null) {
            formData.append(`review[${key}]`, String(value))
          }
        })

        images.forEach((file) => {
          formData.append('review[images][]', file)
        })

        const response = await axios.post(
          `/customer/bookings/${bookingId}/review`,
          formData,
          {
            params: token ? { token } : {},
            withCredentials: !token,
          },
        )
        commit(ReviewMutationTypes.SubmitSuccess, response.data)
      } catch (e: any) {
        const errs = e?.response?.data?.errors
        if (errs) {
          notifications(errorHandler(errs), 'error')
        } else {
          notification("We couldn't submit your review. Please try again.", 'error')
        }
        throw e
      } finally {
        commit(ReviewMutationTypes.SubmitEnd)
      }
    },
  },
  mutations: {
    [ReviewMutationTypes.LoadStarted](state: State) {
      state.reviewLoaded = false
      state.currentCustomerReviewId = null
    },
    [ReviewMutationTypes.GetCustomerReviewSuccess](
      state: State,
      review: Review,
    ) {
      setReview(state, review)
      state.currentCustomerReviewId = review.id
      state.reviewLoaded = true
    },
    [ReviewMutationTypes.SubmitStart](state: State) {
      state.reviewSubmitting = true
    },
    [ReviewMutationTypes.SubmitEnd](state: State) {
      state.reviewSubmitting = false
    },
    [ReviewMutationTypes.SubmitSuccess](state: State, review: Review) {
      setReview(state, review)
      state.currentCustomerReviewId = review.id
      state.reviewLoaded = true
    },
  },
}

function setReview(state: State, review: Review) {
  state.reviewEntities = {
    ...state.reviewEntities,
    [review.id]: review,
  }

  if (state.reviewIds.indexOf(review.id) < 0) {
    state.reviewIds.push(review.id)
  }
}
